// Leichtes SVG-Liniendiagramm für die Monitor-Ansicht — bewusst KEINE Chart-Bibliothek.
//
// Zeichnet mehrere Zeitreihen (je Tag eine Linie) in einem gemeinsamen Koordinatensystem. Die
// Y-Achse skaliert auf die aktuell SICHTBAREN Werte, die X-Achse (Zeit) folgt entweder der vollen
// Datenspanne oder — wenn `xDomain` gesetzt ist — dem gewählten Zeitfenster. Über das Mausrad
// (Zoomen) und Ziehen (Verschieben) meldet der Chart ein neues Zeitfenster per `onXDomainChange`;
// die Zustandshoheit über das Fenster liegt bewusst außen (MonitorView), damit auch die
// Fenster-Auswahl und die Zoom-Knöpfe dasselbe steuern. Der Renderer bleibt rein und über die
// Zahl der Datenpunkte prüfbar.

import { useCallback, useId, useRef, type JSX, type PointerEvent, type WheelEvent } from 'react';

export interface Series {
  tag: string;
  color: string;
  points: { t: number; value: number }[];
}

export type XDomain = [number, number];

interface TrendChartProps {
  series: Series[];
  width?: number;
  height?: number;
  emptyLabel: string;
  // Sichtbares Zeitfenster [tMin, tMax]. Ohne Angabe spannt die X-Achse über alle Datenpunkte.
  xDomain?: XDomain;
  // Meldet ein durch Zoom/Pan gewünschtes neues Zeitfenster (die Aufrufseite klemmt/übernimmt es).
  onXDomainChange?: (domain: XDomain) => void;
}

const PADDING = { top: 12, right: 12, bottom: 22, left: 44 };
const MIN_SPAN = 0.001; // kleinste Zeitspanne (s), damit Zoom-In nicht bis auf 0 kollabiert.

// Zeitspanne über alle Reihen (für die Vorgabe, wenn kein Fenster gesetzt ist).
function timeExtent(series: Series[]): { tMin: number; tMax: number } {
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const s of series) {
    for (const p of s.points) {
      if (p.t < tMin) tMin = p.t;
      if (p.t > tMax) tMax = p.t;
    }
  }
  if (!Number.isFinite(tMin)) {
    tMin = 0;
    tMax = 1;
  }
  if (tMax === tMin) tMax = tMin + 1;
  return { tMin, tMax };
}

// Wertespanne über die im Fenster [tMin, tMax] sichtbaren Punkte; garantiert eine nicht-entartete
// Spanne. Sind keine Punkte im Fenster, wird auf die Spanne aller Punkte zurückgefallen, damit die
// Achse nicht springt, während frische Daten hereinlaufen.
function valueExtent(
  series: Series[],
  tMin: number,
  tMax: number,
): { vMin: number; vMax: number } {
  let vMin = Infinity;
  let vMax = -Infinity;
  const consider = (v: number): void => {
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
  };
  for (const s of series) {
    for (const p of s.points) {
      if (p.t >= tMin && p.t <= tMax) consider(p.value);
    }
  }
  if (!Number.isFinite(vMin)) {
    // Kein Punkt im Fenster → volle Wertespanne, damit die Achse stabil bleibt.
    for (const s of series) for (const p of s.points) consider(p.value);
  }
  if (!Number.isFinite(vMin)) {
    vMin = 0;
    vMax = 1;
  }
  if (vMax === vMin) {
    const pad = Math.abs(vMin) > 0 ? Math.abs(vMin) * 0.1 : 1;
    vMin -= pad;
    vMax += pad;
  }
  return { vMin, vMax };
}

export function TrendChart({
  series,
  width = 640,
  height = 260,
  emptyLabel,
  xDomain,
  onXDomainChange,
}: TrendChartProps): JSX.Element {
  const clipId = useId();
  const hasData = series.some((s) => s.points.length > 0);

  const dataT = timeExtent(series);
  const tMin = xDomain ? xDomain[0] : dataT.tMin;
  const tMax = xDomain ? xDomain[1] : dataT.tMax;
  const span = tMax - tMin > 0 ? tMax - tMin : 1;
  const { vMin, vMax } = valueExtent(series, tMin, tMax);

  const innerW = width - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;

  const xOf = (t: number): number => PADDING.left + ((t - tMin) / span) * innerW;
  const yOf = (v: number): number =>
    PADDING.top + innerH - ((v - vMin) / (vMax - vMin)) * innerH;

  // Interaktion (Zoom/Pan) nur, wenn die Aufrufseite ein Fenster verwaltet.
  const interactive = Boolean(onXDomainChange);
  // Aktuelles Fenster als Ref, damit die Pointer-Handler nicht an veraltete Closures geraten.
  const domainRef = useRef<XDomain>([tMin, tMax]);
  domainRef.current = [tMin, tMax];
  const dragRef = useRef<{ startX: number; d0: XDomain } | null>(null);

  // Bildschirm-X (clientX) → Zeit, anhand der SVG-Bounding-Box (preserveAspectRatio="none").
  const timeAtClientX = useCallback(
    (clientX: number, rect: DOMRect): number => {
      const fx = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
      const svgX = fx * width;
      const [dMin, dMax] = domainRef.current;
      const s = dMax - dMin > 0 ? dMax - dMin : 1;
      return dMin + ((svgX - PADDING.left) / innerW) * s;
    },
    [width, innerW],
  );

  const handleWheel = useCallback(
    (e: WheelEvent<SVGSVGElement>): void => {
      if (!onXDomainChange) return;
      e.preventDefault();
      const rect = e.currentTarget.getBoundingClientRect();
      const [dMin, dMax] = domainRef.current;
      const s = dMax - dMin;
      const anchor = timeAtClientX(e.clientX, rect);
      const factor = e.deltaY < 0 ? 1 / 1.2 : 1.2; // hoch = hineinzoomen
      const newSpan = Math.max(MIN_SPAN, s * factor);
      const ratio = s > 0 ? (anchor - dMin) / s : 0.5;
      const newMin = anchor - ratio * newSpan;
      onXDomainChange([newMin, newMin + newSpan]);
    },
    [onXDomainChange, timeAtClientX],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<SVGSVGElement>): void => {
      if (!onXDomainChange) return;
      // Pointer-Capture hält das Ziehen auch außerhalb der SVG-Fläche zusammen (im Browser);
      // in Testumgebungen (jsdom) fehlt die API — dann einfach ohne Capture ziehen.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = { startX: e.clientX, d0: [...domainRef.current] as XDomain };
    },
    [onXDomainChange],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent<SVGSVGElement>): void => {
      const drag = dragRef.current;
      if (!drag || !onXDomainChange) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const [dMin, dMax] = drag.d0;
      const s = dMax - dMin;
      const dxFrac = rect.width > 0 ? (e.clientX - drag.startX) / rect.width : 0;
      // Ziehen nach rechts verschiebt das Fenster in die Vergangenheit (Inhalt folgt der Hand).
      const shift = -dxFrac * (width / innerW) * s;
      onXDomainChange([dMin + shift, dMax + shift]);
    },
    [onXDomainChange, width, innerW],
  );

  const endDrag = useCallback((e: PointerEvent<SVGSVGElement>): void => {
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  // Waagerechte Gitterlinien mit Wert-Beschriftung (5 Stufen).
  const gridLines = Array.from({ length: 5 }, (_, i) => {
    const v = vMin + ((vMax - vMin) * i) / 4;
    return { v, y: yOf(v) };
  });
  // Senkrechte Zeitachse mit Beschriftung (5 Stufen, Sekunden).
  const timeTicks = Array.from({ length: 5 }, (_, i) => {
    const t = tMin + (span * i) / 4;
    return { t, x: xOf(t) };
  });

  return (
    <svg
      role="img"
      aria-label="Trend"
      data-testid="trend-chart"
      viewBox={`0 0 ${width} ${height}`}
      className={`h-full w-full${interactive ? ' cursor-ew-resize touch-none select-none' : ''}`}
      preserveAspectRatio="none"
      onWheel={interactive ? handleWheel : undefined}
      onPointerDown={interactive ? handlePointerDown : undefined}
      onPointerMove={interactive ? handlePointerMove : undefined}
      onPointerUp={interactive ? endDrag : undefined}
      onPointerCancel={interactive ? endDrag : undefined}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x={PADDING.left} y={PADDING.top} width={innerW} height={innerH} />
        </clipPath>
      </defs>

      {/* Plot-Fläche */}
      <rect
        x={PADDING.left}
        y={PADDING.top}
        width={innerW}
        height={innerH}
        fill="#0E2246"
        stroke="#1A3460"
      />

      {/* Gitter + Werteachse */}
      {gridLines.map((g, i) => (
        <g key={i}>
          <line
            x1={PADDING.left}
            x2={PADDING.left + innerW}
            y1={g.y}
            y2={g.y}
            stroke="#1A3460"
            strokeWidth={1}
          />
          <text x={PADDING.left - 6} y={g.y + 3} textAnchor="end" fontSize={10} fill="#7A8FAF">
            {formatTick(g.v)}
          </text>
        </g>
      ))}

      {/* Zeitachse (X) mit Beschriftung in Sekunden */}
      {timeTicks.map((tk, i) => (
        <text
          key={`t-${i}`}
          x={Math.min(Math.max(tk.x, PADDING.left), PADDING.left + innerW)}
          y={height - 6}
          textAnchor={i === 0 ? 'start' : i === timeTicks.length - 1 ? 'end' : 'middle'}
          fontSize={10}
          fill="#7A8FAF"
        >
          {formatTime(tk.t)}
        </text>
      ))}

      {/* Datenlinien */}
      <g clipPath={`url(#${clipId})`}>
        {series.map((s) =>
          s.points.length === 0 ? null : (
            <polyline
              key={s.tag}
              data-testid={`series-${s.tag}`}
              data-count={s.points.length}
              fill="none"
              stroke={s.color}
              strokeWidth={1.8}
              points={s.points.map((p) => `${xOf(p.t)},${yOf(p.value)}`).join(' ')}
            />
          ),
        )}
      </g>

      {/* Leerer Zustand */}
      {!hasData && (
        <text x={width / 2} y={height / 2} textAnchor="middle" fontSize={12} fill="#7A8FAF">
          {emptyLabel}
        </text>
      )}
    </svg>
  );
}

// Kompakte Achsen-Beschriftung: ganze Zahlen ohne Nachkommastellen, kleine Werte gerundet.
function formatTick(v: number): string {
  if (Math.abs(v) >= 100) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(1);
  return v.toFixed(2);
}

// Zeit-Beschriftung der X-Achse (Sekunden). Große Werte kompakt, kleine mit einer Nachkommastelle.
function formatTime(t: number): string {
  const s = Math.round(t * 10) / 10;
  if (Math.abs(s) >= 100) return `${s.toFixed(0)}s`;
  return `${s.toFixed(1)}s`;
}
